import { useEffect, useRef, useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { fetchResultNotice, lookupAdmissionResult } from '../api/lookupApi';
import {
  IdCardIcon,
  SearchIcon,
  CheckIcon,
  XCircleIcon,
  InfoIcon,
  SpinnerIcon,
} from '../components/icons';
import Modal from '../components/Modal';
import './LookupPage.css';

const NOT_FOUND_MESSAGE = 'Thí sinh không có trong danh sách trúng tuyển.';

const GRID_FIELDS = [
  ['date_of_birth', 'Ngày sinh'],
  ['student_code', 'Mã sinh viên'],
  ['expected_class', 'Lớp dự kiến'],
  ['enrollment_date', 'Ngày nhập học'],
  ['enrollment_time', 'Giờ nhập học'],
  ['year', 'Năm học'],
];

function maskIdentification(value) {
  const str = String(value || '');
  if (str.length <= 6) return str;
  const visible = 3;
  const start = str.slice(0, visible);
  const end = str.slice(-visible);
  return `${start}${'*'.repeat(str.length - visible * 2)}${end}`;
}

function maskDateOfBirth(value) {
  if (!value) return '—';
  const str = String(value);
  const match = str.match(/^(\d{1,2})([/\-.])(\d{1,2})\2(\d{4})$/);
  if (match) {
    const [, day, sep, month, year] = match;
    return `${'*'.repeat(day.length)}${sep}${'*'.repeat(month.length)}${sep}${year}`;
  }
  if (str.length <= 4) return str;
  return `${'*'.repeat(str.length - 4)}${str.slice(-4)}`;
}

function LookupPage() {
  const [identification, setIdentification] = useState('');
  const [captchaToken, setCaptchaToken] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [notice, setNotice] = useState('');
  const [modalMessage, setModalMessage] = useState('');
  const recaptchaRef = useRef(null);
  const resultRef = useRef(null);

  useEffect(() => {
    fetchResultNotice()
      .then((data) => setNotice(data?.content || ''))
      .catch(() => setNotice(''));
  }, []);

  useEffect(() => {
    if ((result || error) && resultRef.current) {
      resultRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [result, error]);

  async function handleSubmit(e) {
    e.preventDefault();

    setResult(null);
    setError('');

    if (!captchaToken) {
      setModalMessage('Vui lòng tick vào ô xác nhận captcha trước khi tra cứu.');
      return;
    }

    setLoading(true);

    try {
      const data = await lookupAdmissionResult({ identification, captchaToken });
      setResult(data);
    } catch (err) {
      if (err.response?.status === 404) {
        setError(NOT_FOUND_MESSAGE);
      } else {
        setError(err.response?.data?.message || 'Có lỗi xảy ra, vui lòng thử lại sau.');
      }
    } finally {
      setLoading(false);
      recaptchaRef.current?.reset();
      setCaptchaToken(null);
    }
  }

  function handleReset() {
    setResult(null);
    setError('');
    setIdentification('');
  }

  return (
    <div className="lookup">
      <section className="stub">
        <div className="eyebrow-row">
          <span className="tag">Trường Đại học Công nghệ Giao thông vận tải</span>
        </div>
        <h2>Tra cứu kết quả trúng tuyển</h2>
        <p className="hint">Nhập số căn cước công dân để tra cứu kết quả</p>

        <form onSubmit={handleSubmit}>
          <div className="field">
            <label htmlFor="identification">Số CCCD</label>
            <div className="field__input-wrap">
              <IdCardIcon className="field__icon" />
              <input
                id="identification"
                type="text"
                inputMode="numeric"
                autoComplete="off"
                maxLength={12}
                value={identification}
                onChange={(e) => setIdentification(e.target.value.replace(/[^0-9]/g, ''))}
                placeholder="038207028289"
                required
              />
            </div>
          </div>

          <div className="captcha-wrap">
            <ReCAPTCHA
              ref={recaptchaRef}
              sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
              onChange={(token) => setCaptchaToken(token)}
              onExpired={() => setCaptchaToken(null)}
            />
          </div>

          <button type="submit" className="btn-submit" disabled={loading}>
            {loading ? (
              <>
                <SpinnerIcon /> Đang tra cứu...
              </>
            ) : (
              <>
                <SearchIcon /> Tra cứu
              </>
            )}
          </button>
        </form>

        <div className="tear">✂ - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -</div>
      </section>

      <div ref={resultRef}>
        {error && (
          <div className="refused">
            <XCircleIcon className="refused__glyph" />            
            <p>{error}</p>            
          </div>
        )}

        {result && (
          <>
            <section className="pass">
              <div className="pass__top">
                <div className="pass__eyebrow-row">
                  <span className="tag">
                    <CheckIcon /> Vé nhập học hợp lệ
                  </span>
                  <span className="code mono">{result.admission_code}</span>
                </div>

                <p className="pass__field-label">Họ và tên</p>
                <h3 className="pass__name">{result.full_name}</h3>

                <div className="route">
                  <div className="from">
                    <p className="pass__field-label">CCCD</p>
                    <p className="val mono">{maskIdentification(identification)}</p>
                  </div>
                  <div className="to">
                    <p className="pass__field-label">Điểm đến — Ngành</p>
                    <p className="val">{result.major}</p>
                  </div>
                </div>
                <div className="route-line" />
              </div>

              <div className="perf" />
              <span className="stamp">
                <CheckIcon /> Trúng tuyển
              </span>

              <div className="pass__grid">
                {GRID_FIELDS.map(([key, label]) => (
                  <div key={key}>
                    <p className="pass__field-label">{label}</p>
                    <p className="val mono">
                      {key === 'date_of_birth' ? maskDateOfBirth(result[key]) : result[key] || '—'}
                    </p>
                  </div>
                ))}
                <div className="g-wide">
                  <p className="pass__field-label">Địa điểm</p>
                  <p className="val text">{result.location || '—'}</p>
                </div>
              </div>

              <div className="pass__actions">
                <button type="button" className="btn-link" onClick={handleReset}>
                  Tra cứu số CCCD khác
                </button>
              </div>
            </section>

            {notice && (
              <div className="notice-attach">
                <div className="notice-card">
                  <div className="notice-card__head">
                    <InfoIcon />
                    <span>Thông tin chung</span>
                  </div>
                  <div dangerouslySetInnerHTML={{ __html: notice }} />
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {modalMessage && (
        <Modal          
          message={modalMessage}
          onClose={() => setModalMessage('')}
        />
      )}
    </div>
  );
}

export default LookupPage;
